<template>
  <BModal
    id="modal-detail-category"
    ref="modal-detail-category"
    no-stacking
    centered
    hide-footer
    hide-header
    hide-header-close
    no-close-on-backdrop
    no-close-on-esc
    size="xl"
    body-class="p-4"
  >
    <b-row class="justify-content-end">
      <b-img
        src="https://storage.googleapis.com/komerce/assets/icons/close-circle.svg"
        class="cursor-pointer"
        @click="closeModal()"
      />
    </b-row>

    <div class="modal-content-wrapper">
      <!-- Header Section -->
      <div class="modal-header-section">
        <h2 class="modal-title">
          Detail Kategori Bisnis
        </h2>
        <p class="modal-subtitle">
          Komads telah mengelompokkan berbagai aspek ke dalam kategori bisnis
          utama. Kamu bisa lihat detailnya di sini dan pilih kategori yang
          sesuai dengan kebutuhanmu.
        </p>
      </div>

      <!-- Filter Section -->
      <div class="filter-section">
        <BFormGroup class="category-filter">
          <template #label>
            <span class="filter-label">Pilih Kategori Bisnis</span>
          </template>
          <BFormSelect
            v-model="selectedCategory"
            :options="categoryOptions"
            class="category-select capitalize"
            type="primary"
            @change="scrollToCategory"
          />
        </BFormGroup>
      </div>

      <!-- Table Section -->
      <div class="table-container">
        <table class="category-table">
          <thead class="bg-light">
            <tr>
              <th
                class="text-center"
                style="width: 60px;"
              >
                No
              </th>
              <th
                class="text-center"
                style="width: 200px;"
              >
                Kategori Bisnis
              </th>
              <th class="text-center">
                Detail Kategori Bisnis
              </th>
              <th class="justify-center flex flex-row gap-2">
                Estimasi Penonton
                <img
                  v-b-tooltip.hover.top="
                    'Estimasi Penonton bisa berubah ketika dipadukan dengan Umur, Jenis Kelamin, dan Lokasi'
                  "
                  src="https://storage.googleapis.com/komerce/assets/icons/info-circle.svg"
                  alt="Komerce"
                  width="15"
                >
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading || businessCategories.length === 0">
              <td
                colspan="4"
                class="text-center py-3"
              >
                <div v-if="isLoading">
                  Memuat data kategori...
                </div>
                <div v-else>
                  Tidak ada data kategori yang tersedia
                </div>
              </td>
            </tr>
            <template
              v-for="(category, categoryIndex) in processedCategories"
              v-else
            >
              <tr
                v-for="(detail, detailIndex) in category.sub_categories"
                :key="categoryIndex + '-' + detailIndex"
              >
                <td
                  v-if="detailIndex === 0"
                  :rowspan="category.sub_categories.length"
                  class="text-center align-middle"
                >
                  {{ categoryIndex + 1 }}
                </td>
                <td
                  v-if="detailIndex === 0"
                  :id="
                    'category-' +
                      category.category_business
                        .replace(/\s+/g, '-')
                        .toLowerCase()
                  "
                  :rowspan="category.sub_categories.length"
                  class="align-middle font-weight-medium capitalize"
                >
                  {{ category.category_business }}
                </td>
                <td class="align-middle capitalize">
                  {{ detail.name }}
                </td>
                <td
                  v-if="detailIndex === 0"
                  :rowspan="category.sub_categories.length"
                  class="text-center align-middle"
                >
                  {{ category.reach_estimate }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </BModal>
</template>

<script>
import { newAxiosIns } from '@/libs/axios'

export default {
  name: 'ModalDetailCategory',
  data() {
    return {
      selectedCategory: '',
      businessCategories: [],
    }
  },
  computed: {
    /**
     * Generate category options for dropdown
     * @returns {Array} Array of dropdown options
     */
    categoryOptions() {
      const options = [{ value: '', text: 'Semua Kategori' }]
      this.businessCategories.forEach(category => {
        options.push({
          value: category.category_business,
          text: category.category_business,
        })
      })
      return options
    },

    /**
     * Generate processed categories for table with rowspan
     * @returns {Array} Array of categories with details for rowspan table
     */
    processedCategories() {
      return this.businessCategories
        .filter(category => {
          // First filter by selected category if any
          if (
            this.selectedCategory
            && category.category_business !== this.selectedCategory
          ) {
            return false
          }

          return true
        })
        .map(category => {
          if (!this.selectedCategory) {
            return {
              ...category,
            }
          }
          return category
        })
    },
  },
  mounted() {
    this.fetchCategories()
  },
  methods: {
    /**
     * Fetch categories data from API
     */
    async fetchCategories() {
      try {
        this.isLoading = true
        const response = await newAxiosIns.get(
          '/komads/api/v1/categories/detail',
        )

        if (
          response.data
          && response.data.data
          && Array.isArray(response.data.data)
        ) {
          if (response.data.data.length === 0) {
            this.businessCategories = []
            return
          }

          this.businessCategories = response.data.data
            .map(category => {
              // Validate category structure
              if (!category.name || !Array.isArray(category.sub_categories)) {
                return null
              }

              return {
                category_business: category.name,
                sub_categories: category.sub_categories
                  .filter(subCategory => subCategory && subCategory.name),
                reach_estimate: category.reach_estimate,
              }
            })
            .filter(Boolean) // Remove null entries
        }
      } catch (error) {
        this.businessCategories = []
      } finally {
        this.isLoading = false

        if (this.selectedCategory) {
          this.$nextTick(() => {
            this.scrollToCategory()
          })
        }
      }
    },

    /**
     * Scroll to selected category in the table
     */
    scrollToCategory() {
      if (this.selectedCategory) {
        // Wait for the filtered data to be rendered
        this.$nextTick(() => {
          const elementId = `category-${this.selectedCategory
            .replace(/\s+/g, '-')
            .toLowerCase()}`
          const element = document.getElementById(elementId)

          if (element) {
            // Scroll to the element with smooth behavior
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            // Highlight the category temporarily
            element.classList.add('highlight-category')

            // Remove highlight after animation completes
            setTimeout(() => {
              element.classList.remove('highlight-category')
            }, 2000)
          }
        })
      }
    },

    closeModal() {
      this.selectedCategory = ''
      this.$bvModal.hide('modal-detail-category')
    },
  },
}
</script>

<style lang="scss" scoped src="../Komads.scss" />
